import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { listPages, getPageView } from '../api/customReports';
import { CompareTable } from './FinancialsSynthesisSimple';
import { buildReportTable } from './CustomReportsPage';
import PrintReportHeader from './hr/print/PrintReportHeader';
import './financials/financialsScreenPrint.css';
import './CustomReportsPage.css';

/* Rendu auto-suffisant des rapports d'UNE page Points d'attention : fetch la vue
   de la page puis rend chaque card en CompareTable. Source unique réutilisée par
   l'export Excel (1 page = 1 feuille, via ExcelExportButton) et par le print PDF
   (une section A4 par page). Le mapping card→table vient de buildReportTable
   (CustomReportsPage), donc l'export colle toujours à l'écran. */
export function CustomReportPageTables({ tenantId, pageId, year, unit = 'kchf', onReady }) {
  const { t } = useTranslation();
  const p = useP();
  const [cards, setCards] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!tenantId || !pageId) { setCards([]); if (onReady) onReady(); return undefined; }
    getPageView(tenantId, pageId, year).then((v) => {
      if (!alive) return;
      setCards(v?.error ? [] : (v?.data?.cards || []));
      if (onReady) onReady();
    });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tenantId, pageId, year]);

  if (cards == null) return <p className="page-table-loading">{t('common:status.loading')}</p>;
  if (cards.length === 0) return <p className="custrep-note">{p('Aucun rapport sur cette page.')}</p>;
  return (
    <>
      {cards.map((card) => {
        const { groups, rows, columns } = buildReportTable(card);
        return (
          <div key={card.report_id} className="custrep-print-block">
            <div className="custrep-print-title">{card.name}</div>
            {columns.length > 0 && rows.length > 0
              ? <CompareTable groups={groups} rows={rows} unit={unit} compact />
              : <p className="custrep-note">{p('Rapport vide.')}</p>}
          </div>
        );
      })}
    </>
  );
}

/* Rapport print « Points d'attention » : TOUTES les pages de la section, une
   section A4 par page (en-tête = nom de page + tables). Notifie onDataReady une
   fois toutes les pages chargées (ou tout de suite s'il n'y a aucune page).
   Entrée registre : key 'custom-reports'. */
export default function CustomReportsPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id || null;
  const [pages, setPages] = useState(null);
  const done = useRef(0);
  const fired = useRef(false);

  useEffect(() => {
    let alive = true;
    if (!tenantId) { setPages([]); return undefined; }
    listPages(tenantId).then((r) => { if (alive) setPages(r?.error ? [] : (r?.data || [])); });
    return () => { alive = false; };
  }, [tenantId]);

  useEffect(() => {
    if (pages && pages.length === 0 && !fired.current) {
      fired.current = true;
      if (onDataReady) onDataReady('custom-reports');
    }
  }, [pages, onDataReady]);

  const pageReady = () => {
    done.current += 1;
    if (pages && done.current >= pages.length && !fired.current) {
      fired.current = true;
      if (onDataReady) onDataReady('custom-reports');
    }
  };

  if (pages == null) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;
  return (
    <>
      {pages.map((pg) => (
        <section key={pg.id} className="ppl-print__section ppl-print__section--wide fin-screenprint">
          <PrintReportHeader category={p("Finance · points d'attention")} title={pg.name} year={year} tenant={activeTenant} editedAt={editedAt} />
          <CustomReportPageTables tenantId={tenantId} pageId={pg.id} year={year} unit="kchf" onReady={pageReady} />
        </section>
      ))}
    </>
  );
}
